import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { ROLE_OPTIONS } from "@/lib/utils-msip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export const roleKeyOf = (u) => ROLE_OPTIONS.find((o) => o.role === u.role && (o.department || null) === (u.department || null))?.key || "manager_sales";

// Which scope block a role needs — the one rule shared by the Create form and the Edit dialog.
export const scopeKind = (opt) => {
  if (!opt) return null;
  const service = opt.department === "service" && (opt.role === "zonal" || opt.role === "manager");
  if (service) return "workshops";
  if (opt.role === "zonal" && opt.department === "sales") return "branches";
  if (opt.role === "manager" && opt.department === "sales") return "dealers";
  return null;
};

const SCOPE_RULE = {
  branches: "Zonal Managers (Sales) see the dealers and RTOs behind their assigned outlets.",
  dealers: "Sales Managers see only their mapped dealers' RTOs and sales figures.",
  workshops: "Service roles see the workshops of their dealer codes — only dealers flagged as workshops are listed (turn one on under Admin › Service if missing).",
};

// Service roles may only carry workshop dealer codes — drop anything the chips do not offer.
export const allowedDealerCodes = (kind, dealers, codes) => (kind === "workshops" ? codes.filter((c) => dealers.some((d) => d.code === c && d.has_workshop)) : codes);

export function ScopePicker({ kind, branches, dealers, branchNames, dealerCodes, onChange, idPrefix = "user" }) {
  if (!kind) return null;
  const chip = (on) => `rounded border px-2 py-1 text-xs transition-colors duration-200 ${on ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:bg-secondary"}`;
  if (kind === "branches") {
    const direct = dealers.filter((d) => ["Studio", "Festive"].includes(d.facility) || dealerCodes.includes(d.code));
    return (
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1.5" data-testid={`${idPrefix}-branch-count`}>Assigned outlets — sales coverage ({branchNames.length} selected)</label>
          <div className="flex flex-wrap gap-1.5">
            {branches.map((b) => {
              const on = branchNames.includes(b.branch);
              return (
                <button key={b.branch} type="button" className={chip(on)} data-testid={`${idPrefix}-branch-${b.branch}`}
                  onClick={() => onChange({ branch_names: on ? branchNames.filter((x) => x !== b.branch) : [...branchNames, b.branch] })}>
                  {b.display}
                </button>
              );
            })}
          </div>
        </div>
        {direct.length > 0 && (
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1.5" data-testid={`${idPrefix}-studio-count`}>K Studios / festive counters — covered directly ({dealerCodes.length} selected)</label>
            <div className="flex flex-wrap gap-1.5">
              {direct.map((d) => {
                const on = dealerCodes.includes(d.code);
                return (
                  <button key={d.code} type="button" className={chip(on)} title={d.name} data-testid={`${idPrefix}-dealer-${d.code}`}
                    onClick={() => onChange({ dealer_codes: on ? dealerCodes.filter((c) => c !== d.code) : [...dealerCodes, d.code] })}>
                    {d.code}<span className={`ml-1 text-[10px] ${on ? "opacity-80" : "opacity-60"}`}>{d.name} · {d.facility}</span>
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">Studios have no outlet in the HR manpower workbook, so tick them here — the zonal head then sees their DPC plans, approvals and figures like any other dealer.</p>
          </div>
        )}
      </div>
    );
  }
  const service = kind === "workshops";
  const choices = service ? dealers.filter((d) => d.has_workshop) : dealers;
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground block mb-1.5" data-testid={`${idPrefix}-dealer-count`}>{service ? "Workshops — dealer codes" : "Mapped dealers"} ({dealerCodes.length} selected)</label>
      <div className="flex flex-wrap gap-1.5">
        {choices.map((d) => {
          const on = dealerCodes.includes(d.code);
          return (
            <button key={d.code} type="button" className={chip(on)} title={d.name} data-testid={`${idPrefix}-dealer-${d.code}`}
              onClick={() => onChange({ dealer_codes: on ? dealerCodes.filter((c) => c !== d.code) : [...dealerCodes, d.code] })}>
              {d.code}{service && <span className={`ml-1 text-[10px] ${on ? "opacity-80" : "opacity-60"}`}>{d.name}</span>}
            </button>
          );
        })}
      </div>
      {service && <p className="mt-1 text-[11px] text-muted-foreground">Only dealers flagged as workshops are listed — turn a workshop on under Admin › Service if one is missing.</p>}
    </div>
  );
}

export function UserEditDialog({ user: u, me, branches, dealers, onClose, onSaved }) {
  const [form, setForm] = useState({ name: u.name || "", roleKey: roleKeyOf(u), branch_names: u.branch_names || [], dealer_codes: u.dealer_codes || [], password: "" });
  const [saving, setSaving] = useState(false);
  const opt = ROLE_OPTIONS.find((o) => o.key === form.roleKey);
  const kind = scopeKind(opt);
  const isSelf = u.id === me?.id;
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const changeRole = (roleKey) => {
    const k = scopeKind(ROLE_OPTIONS.find((o) => o.key === roleKey));
    setForm((f) => ({ ...f, roleKey, dealer_codes: allowedDealerCodes(k, dealers, f.dealer_codes) }));
  };

  const save = async () => {
    if (form.password && form.password.length < 8) return toast.error("New password must be at least 8 characters");
    setSaving(true);
    try {
      await api.put(`/admin/users/${u.id}`, {
        name: form.name.trim(), role: opt.role, department: opt.department,
        branch_names: kind === "branches" ? form.branch_names : [],
        dealer_codes: kind ? allowedDealerCodes(kind, dealers, form.dealer_codes) : [],
        ...(form.password ? { password: form.password } : {}),
      });
      toast.success("Account updated");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit account — {u.email}</DialogTitle>
          <DialogDescription>Change the name, role, scope or password in place. Email is the login identity and stays fixed. Changes apply from the person's next page load.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Name</label>
              <Input value={form.name} onChange={(e) => patch({ name: e.target.value })} className="h-9 w-48 text-sm" data-testid="edit-user-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Role</label>
              <Select value={form.roleKey} onValueChange={changeRole} disabled={isSelf}>
                <SelectTrigger className="h-9 w-56 text-sm" data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Set new password <span className="font-normal">(blank = keep current)</span></label>
              <Input type="password" value={form.password} onChange={(e) => patch({ password: e.target.value })} placeholder="min 8 characters" autoComplete="new-password" className="h-9 w-48 text-sm" data-testid="edit-user-password-input" />
            </div>
          </div>
          {isSelf && <p className="text-[11px] text-muted-foreground" data-testid="edit-user-self-note">You cannot change your own role.</p>}
          {kind ? (
            <ScopePicker kind={kind} branches={branches} dealers={dealers} branchNames={form.branch_names} dealerCodes={form.dealer_codes} onChange={patch} idPrefix="edit-user" />
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="edit-user-no-scope">{opt?.label} has no scope to assign — this role sees {opt?.role === "uploader" ? "the upload portal only" : opt?.role === "stock" ? "the whole stock module" : "all markets and dealers"}.</p>
          )}
          {kind && <p className="text-[11px] text-muted-foreground">{SCOPE_RULE[kind]}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="edit-user-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name.trim()} data-testid="edit-user-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
